// App.js
import React, { Component, lazy, Suspense } from 'react';
import { connect } from 'react-redux';
import { BrowserRouter as Router, Link, Route, Routes } from 'react-router-dom';
import './App.css';
import axios from 'axios';
import { login, logout } from './actions/auth';
import AuthService from './services/auth.service';
import { clearMessage } from './actions/message';
import { history } from './helpers/history';
import { getStoredUser } from './helpers/storage';
import { attachAuthInterceptors } from './api/axios-auth';
import http from './http-common';
import Logo from './theme/img/logo/logo_blue.png';
import LogoHome from './theme/img/logo/logo_case.png';
import avatar from './theme/img/gritter/settings.png';
import $ from 'jquery';
import { MsalProvider } from '@azure/msal-react';
import { withAuth0 } from '@auth0/auth0-react';
import RequesterDoc from './components/case/file/ManualEN_Requester_v 1.9.0.pdf';
import ResponsibleDoc from './components/case/file/ManualEN_Responsible_v 1.9.0.pdf';
import TestImg from './components/case/file/test.png';
import AuthenticatedRoutes from './components/app/AuthenticatedRoutes';
import RoutePageFallback from './components/app/RoutePageFallback';

const Login = lazy(() => import('./components/login.component'));
const Register = lazy(() => import('./components/register.component'));
const EscalationPublicView = lazy(() => import('./components/escalation/EscalationPublicView'));
const EscalationPublicCaseView = lazy(() => import('./components/escalation/EscalationPublicCaseView'));
const ServiceRequest = lazy(() => import('./components/case/service_request.component'));
const ServiceRequestIntegration = lazy(() => import('./components/service_request_integration.component'));
const NotUser = lazy(() => import('./components/case/modal/notUser.component'));
const NotPermission = lazy(() => import('./components/case/modal/notPermission.component'));

window.jQuery = $;
require('jquery-slimscroll');
require('bootstrap');

class App extends Component {
    constructor(props) {
        super(props);
        this.timeOut = null;
        this.tokenCheckInterval = null;
        this.tokenWarningShown = false;
        this._handlingTokenExpiry = false; // guard: prevent multiple simultaneous calls

        this.logOut = this.logOut.bind(this);
        this.stopViewAsUser = this.stopViewAsUser.bind(this);

        this.state = {
            currentUser: undefined,
            title: '',
            description: '',
            collapsed: false,
            is_mobile: false,
            is_mini_menu: false,
            is_fixed_header: false,
            responsiveFunctions: [],
            isAdmin: false,
            isUser: false,
            isProductOwner: false,
            isModerator: false,
            isServiceRequester: false,
            isServiceReviewer: false,
            isServiceApprover: false,
            isPlanner: false,
            isFinalReviewer: false,
            isFinalApprover: false,
            isCustomerRepresentative: false,
            isSales: false,
            userLogin: false,
            responseSync: null,
            checkSameUser: false,
            isNotUser: false,
            checkRole: false,
            release: process.env.REACT_APP_REVISION,
            isLogedAuth0: false,
            logoutURL: process.env.REACT_APP_OFA_REDIRECT,
            showTooltip: false,
            showFloating: false,
        };

        const self = this;
        self.logOut = this.logOut.bind(self);
        self.serviceRequestCallback = self.serviceRequestCallback.bind(self);

        // ========== เพิ่ม: Setup Axios Interceptors ==========
        self.setupAxiosInterceptors();
        // ====================================================

        $(document).ready(() => {
            self.checkLayout();
            self.handleSidebar();
            self.handleSidebarCollapse();
            self.responsiveSidebar();
            self.handleSlimScrolls();
            self.handleTeamView();
            self.handleHomePageTooltips();
            self.handlePopovers();
            self.handleAlerts();
        });

        $(window).resize(() => {
            setTimeout(() => {
                self.checkLayout();
                self.handleSidebarAndContentHeight();
                self.responsiveSidebar();
                self.handleFixedSidebar();
                self.handleNavbarFixedTop();
                self.runResponsiveFunctions();
            }, 50);
        });

        history.listen((location) => {
            props.dispatch(clearMessage());
        });
    }

    // ========== เพิ่มฟังก์ชันใหม่สำหรับจัดการ Token ==========

    /**
     * Setup Axios Interceptors เพื่อจัดการ Token Expiration
     */
    setupAxiosInterceptors() {
        const on401 = () => {
            console.warn('⚠️ Token expired (401), logging out...');
            this.handleTokenExpired();
        };
        attachAuthInterceptors(axios, on401);
        attachAuthInterceptors(http, on401);
    }

    /**
     * ตรวจสอบว่า adminSession backup ยังไม่หมดอายุ
     */
    isAdminSessionBackupValid() {
        try {
            const raw = localStorage.getItem('adminSession');
            if (!raw) return false;
            const session = JSON.parse(raw);
            if (!session || !session.accessToken) return false;
            const parts = session.accessToken.split('.');
            if (parts.length !== 3) return false;
            const payload = JSON.parse(atob(parts[1]));
            return payload.exp * 1000 > Date.now();
        } catch {
            return false;
        }
    }

    /**
     * จัดการเมื่อ Token หมดอายุ
     * ถ้าอยู่ใน view-as-user mode ให้ลอง restore admin session จาก backup ก่อน
     */
    handleTokenExpired() {
        // Guard: ถ้ากำลังจัดการอยู่แล้ว (multiple 401 responses in flight) ให้ข้ามไป
        if (this._handlingTokenExpiry) return;
        this._handlingTokenExpiry = true;

        console.log('🔒 Handling token expiration...');

        const currentUser = getStoredUser();
        const isImpersonating = currentUser && currentUser.impersonatedBy;

        // ถ้ากำลัง view-as-user อยู่ ให้ลอง restore admin session จาก backup
        if (isImpersonating && this.isAdminSessionBackupValid()) {
            console.log('🔄 Impersonation session expired — restoring admin session from backup...');
            const adminSession = localStorage.getItem('adminSession');
            localStorage.setItem('user', adminSession);
            localStorage.removeItem('adminSession');
            this.stopTokenCheck();
            this.showImpersonationRestoredAlert();
            return;
        }

        // หยุดการตรวจสอบ token
        this.stopTokenCheck();

        // Clear timeout เดิม
        if (this.timeOut) {
            clearTimeout(this.timeOut);
            this.timeOut = null;
        }

        // Clear localStorage
        localStorage.removeItem('user');
        localStorage.removeItem('loginType');
        localStorage.removeItem('adminSession');
        localStorage.removeItem('smartOFA');
        localStorage.removeItem('mini_sidebar');
        localStorage.removeItem('service_request');

        // Update state
        this.setState({
            currentUser: undefined,
            isUser: false,
            isAdmin: false,
            isModerator: false,
            isProductOwner: false,
            isServiceRequester: false,
            isServiceApprover: false,
            isPlanner: false,
            isFinalReviewer: false,
            isFinalApprover: false,
            isCustomerRepresentative: false,
            isSales: false,
        });

        // Dispatch Redux logout
        this.props.dispatch(logout());

        // แสดงข้อความแจ้งเตือน
        if (isImpersonating) {
            this.showImpersonationExpiredAlert();
        } else {
            this.showSessionExpiredAlert();
        }
    }

    /**
     * แสดงการแจ้งเตือนเมื่อ restore admin session จาก backup สำเร็จ
     */
    showImpersonationRestoredAlert() {
        const title = 'View-as-User Session Expired';
        const msg = 'The view-as-user session has expired. You have been returned to your admin session.';
        if (window.Swal) {
            window.Swal.fire({
                icon: 'warning',
                title,
                text: msg,
                confirmButtonText: 'OK',
                allowOutsideClick: false,
                customClass: { confirmButton: 'btn btn-primary' },
            }).then(() => {
                history.push('/');
                window.location.reload();
            });
        } else {
            alert('⚠️ ' + msg);
            history.push('/');
            window.location.reload();
        }
    }

    /**
     * แสดงการแจ้งเตือนเมื่อ view-as-user session หมดอายุและไม่มี backup admin session
     * (ต้อง login ใหม่)
     */
    showImpersonationExpiredAlert() {
        const title = 'View-as-User Session Expired';
        const msg = 'The view-as-user session has expired. Please log in again to continue.';
        if (window.Swal) {
            window.Swal.fire({
                icon: 'warning',
                title,
                text: msg,
                confirmButtonText: 'OK',
                allowOutsideClick: false,
                customClass: { confirmButton: 'btn btn-primary' },
            }).then(() => {
                history.push('/login');
                window.location.reload();
            });
        } else {
            alert('⚠️ ' + msg);
            history.push('/login');
            window.location.reload();
        }
    }

    /**
     * แสดงการแจ้งเตือนเมื่อ Session หมดอายุ
     */
    showSessionExpiredAlert() {
        // ใช้ SweetAlert2 ถ้ามี
        if (window.Swal) {
            window.Swal.fire({
                icon: 'warning',
                title: 'Session Expired',
                text: 'Your session has expired. Please login again.',
                confirmButtonText: 'OK',
                allowOutsideClick: false,
                customClass: {
                    confirmButton: 'btn btn-primary',
                },
            }).then(() => {
                history.push('/login');
                window.location.reload();
            });
        } else {
            // ใช้ alert ธรรมดา
            alert('⚠️ Your session has expired. Please login again.');
            history.push('/login');
            window.location.reload();
        }
    }

    /**
     * ตรวจสอบ Token Expiration แบบ Proactive
     */
    checkTokenExpiration() {
        const user = getStoredUser();

        if (!user || !user.accessToken) {
            return;
        }

        try {
            const tokenParts = user.accessToken.split('.');
            if (tokenParts.length !== 3) {
                console.error('❌ Invalid token format');
                return;
            }

            const tokenPayload = JSON.parse(atob(tokenParts[1]));
            const expirationTime = tokenPayload.exp * 1000;
            const currentTime = Date.now();
            const timeRemaining = expirationTime - currentTime;

            if (timeRemaining <= 0) {
                console.warn('❌ Token has expired');
                this.handleTokenExpired();
                return;
            }

            // ถ้าเหลือเวลาน้อยกว่า 2 นาที ให้เตือน (ข้ามใน view-as-user mode เพราะจะ restore admin session แทน)
            if (timeRemaining < 2 * 60 * 1000 && !user.impersonatedBy) {
                console.warn('⚠️ Token will expire soon:', new Date(expirationTime));
                this.showTokenExpiringWarning();
            }
        } catch (error) {
            console.error('❌ Error checking token expiration:', error);
        }
    }

    /**
     * แสดงการเตือนว่า Token ใกล้หมดอายุ (Optional)
     */
    showTokenExpiringWarning() {
        // แสดงเฉพาะครั้งแรก
        if (!this.tokenWarningShown) {
            this.tokenWarningShown = true;

            if (window.Swal) {
                window.Swal.fire({
                    icon: 'info',
                    title: 'Session Expiring Soon',
                    text: 'Your session will expire soon. Please save your work.',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 5000,
                    timerProgressBar: true,
                });
            }
        }
    }

    /**
     * เริ่มตรวจสอบ Token ทุกๆ 30 วินาที
     */
    startTokenCheck() {
        console.log('✅ Starting token check...');

        // Check ทันทีตอนเริ่ม
        this.checkTokenExpiration();

        // Check ทุก 30 วินาที
        this.tokenCheckInterval = setInterval(() => {
            this.checkTokenExpiration();
        }, 30000); // 30 seconds
    }

    /**
     * หยุดการตรวจสอบ Token
     */
    stopTokenCheck() {
        if (this.tokenCheckInterval) {
            console.log('🛑 Stopping token check...');
            clearInterval(this.tokenCheckInterval);
            this.tokenCheckInterval = null;
            this.tokenWarningShown = false;
        }
    }

    // ========== แก้ไข componentDidMount ==========
    async componentDidMount() {
        const user = this.props.user;
        if (user) {
            this.setState({
                currentUser: user,
                isUser: true,
                isModerator: user.roles.includes('ROLE_MODERATOR'),
                isAdmin: user.roles.includes('ROLE_ADMIN'),
                isPlanner: user.roles.includes('ROLE_REPCO_PLANNER'),
                isServiceRequester: user.roles.includes('ROLE_SERVICE_REQUESTER'),
                isServiceApprover: user.roles.includes('ROLE_SERVICE_APPROVAL'),
                isFinalReviewer: user.roles.includes('ROLE_FINAL_REVIEWER'),
                isFinalApprover: user.roles.includes('ROLE_FINAL_APPROVER'),
                isProductOwner: user.product_owner ? (user.product_owner.length > 0 ? true : false) : false,
                isCustomerRepresentative: user.roles.includes('ROLE_CUSTOMER_REPRESENTATIVE'),
                isSales: user.roles.includes('ROLE_SALES'),
            });

            // ========== เพิ่มส่วนนี้ ==========
            // เริ่มตรวจสอบ token
            this.startTokenCheck();
            // ==================================
        }
    }

    // ========== เพิ่ม componentWillUnmount ==========
    componentWillUnmount() {
        // หยุดการตรวจสอบ token
        this.stopTokenCheck();

        // Clear timeout เดิม
        if (this.timeOut) {
            clearTimeout(this.timeOut);
        }
    }

    // ========== ฟังก์ชัน Theme เดิมทั้งหมด ไม่ต้องแก้ ==========

    checkLayout() {
        this.setState({
            is_mini_menu: $('#sidebar').hasClass('mini-menu'),
            is_fixed_header: $('#header').hasClass('navbar-fixed-top'),
        });
    }

    handleSidebar() {
        const self = this;
        $('.sidebar-menu .has-sub > a').click(function () {
            let last = $('.has-sub.open', $('.sidebar-menu'));
            last.removeClass('open');
            $('.arrow', last).removeClass('open');
            $('.sub', last).slideUp(200);
            let thisElement = $(this);
            let slideOffeset = -200;
            let slideSpeed = 200;
            let sub = $(this).next();
            if (sub.is(':visible')) {
                $('.arrow', $(this)).removeClass('open');
                $(this).parent().removeClass('open');
                sub.slideUp(slideSpeed, function () {
                    if ($('#sidebar').hasClass('sidebar-fixed') === false) {
                        self.scrollTo(thisElement, slideOffeset);
                    }
                    self.handleSidebarAndContentHeight();
                });
            } else {
                $('.arrow', $(this)).addClass('open');
                $(this).parent().addClass('open');
                sub.slideDown(slideSpeed, function () {
                    if ($('#sidebar').hasClass('sidebar-fixed') === false) {
                        self.scrollTo(thisElement, slideOffeset);
                    }
                    self.handleSidebarAndContentHeight();
                });
            }
        });
        $('.sidebar-menu .has-sub .sub .has-sub-sub > a').click(function () {
            let last = $('.has-sub-sub.open', $('.sidebar-menu'));
            last.removeClass('open');
            $('.arrow', last).removeClass('open');
            // Fix: ใช้ .sub-sub ไม่ใช่ .sub เพราะ submenu ระดับนี้ใช้ class sub-sub
            $('.sub-sub', last).slideUp(200);
            let sub = $(this).next();
            if (sub.is(':visible')) {
                $('.arrow', $(this)).removeClass('open');
                $(this).parent().removeClass('open');
                sub.slideUp(200);
            } else {
                $('.arrow', $(this)).addClass('open');
                $(this).parent().addClass('open');
                sub.slideDown(200);
            }
        });
    }

    handleSidebarCollapse() {
        let self = this;
        let viewport = self.getViewPort();
        if (localStorage.getItem('mini_sidebar') === '1') {
            $('.navbar-brand').addClass('mini-menu');
            $('#sidebar').addClass('mini-menu');
            $('#main-content').addClass('margin-left-50');
            self.setState({
                collapsed: true,
            });
        }
        $('.sidebar-collapse').click(function () {
            if (self.state.is_mobile && !self.state.is_mini_menu) {
                if (self.state.collapsed) {
                    $('body').removeClass('slidebar');
                    $('.sidebar').removeClass('sidebar-fixed');
                    if (self.state.is_fixed_header) {
                        $('#header').addClass('navbar-fixed-top');
                        $('#main-content').addClass('margin-top-100');
                    }
                    self.setState({ collapsed: false });
                    localStorage.setItem('mini_sidebar', '0');
                } else {
                    $('body').addClass('slidebar');
                    $('.sidebar').addClass('sidebar-fixed');
                    if (self.state.is_fixed_header) {
                        $('#header').removeClass('navbar-fixed-top');
                        $('#main-content').removeClass('margin-top-100');
                    }
                    self.setState({ collapsed: true });
                    localStorage.setItem('mini_sidebar', '1');
                    self.handleMobileSidebar();
                }
            } else {
                let iconElem = document.getElementById('sidebar-collapse').querySelector('[class*="fa-"]');
                let iconLeft = iconElem.getAttribute('data-icon1');
                let iconRight = iconElem.getAttribute('data-icon2');
                if (self.state.collapsed) {
                    $('.navbar-brand').removeClass('mini-menu');
                    $('#sidebar').removeClass('mini-menu');
                    $('#main-content').removeClass('margin-left-50');
                    $('.sidebar-collapse i').removeClass(iconRight);
                    $('.sidebar-collapse i').addClass(iconLeft);
                    $('.search').attr('placeholder', 'Search');
                    self.setState({ collapsed: false });
                    localStorage.setItem('mini_sidebar', '0');
                } else {
                    $('.navbar-brand').addClass('mini-menu');
                    $('#sidebar').addClass('mini-menu');
                    $('#main-content').addClass('margin-left-50');
                    $('.sidebar-collapse i').removeClass(iconLeft);
                    $('.sidebar-collapse i').addClass(iconRight);
                    $('.search').attr('placeholder', '');
                    self.setState({ collapsed: true });
                    localStorage.setItem('mini_sidebar', '1');
                }
                $('#main-content').on('resize', function (e) {
                    e.stopPropagation();
                });
            }
        });
    }

    handleSidebarAndContentHeight() {
        let content = $('#content');
        let sidebar = $('#sidebar');
        let body = $('body');
        let height;
        // Ensure the left border (on #content) spans when viewport is taller than the sidebar/content.
        // Use the larger of: viewport height (minus header) and sidebar height.
        const viewportHeight = $(window).height() - $('#header').height() + 1;
        const sidebarHeight = sidebar.height() + 20;
        height = body.hasClass('sidebar-fixed') ? viewportHeight : Math.max(viewportHeight, sidebarHeight);
        if (height >= content.height()) {
            content.attr('style', 'min-height:' + height + 'px !important');
        }
    }

    getViewPort() {
        let e = window,
            a = 'inner';
        if (!('innerWidth' in window)) {
            a = 'client';
            e = document.documentElement || document.body;
        }
        return {
            width: e[a + 'Width'],
            height: e[a + 'Height'],
        };
    }

    handleMobileSidebar() {
        let menu = $('.sidebar');
        if (menu.parent('.slimScrollDiv').length === 1) {
            menu.slimScroll({
                destroy: true,
            });
            menu.removeAttr('style');
            $('#sidebar').removeAttr('style');
        }
        menu.slimScroll({
            size: '7px',
            color: '#a1b2bd',
            opacity: 0.3,
            height: '100%',
            allowPageScroll: false,
            disableFadeOut: false,
        });
    }

    responsiveSidebar() {
        let self = this;
        let width = $(window).width();
        if (width < 768) {
            self.setState({ is_mobile: true });
            $('#main-content').addClass('margin-left-0');
        } else {
            self.setState({ is_mobile: false });
            $('#main-content').removeClass('margin-left-0');
            let menu = $('.sidebar');
            if (menu.parent('.slimScrollDiv').length === 1) {
                menu.slimScroll({
                    destroy: true,
                });
                menu.removeAttr('style');
                $('#sidebar').removeAttr('style');
            }
        }
    }

    handleSlimScrolls() {
        if (!$().slimScroll) {
            return;
        }
        $('.scroller').each(function () {
            $(this).slimScroll({
                size: '7px',
                color: '#a1b2bd',
                height: $(this).attr('data-height'),
                alwaysVisible: $(this).attr('data-always-visible') == '1' ? true : false,
                railVisible: $(this).attr('data-rail-visible') == '1' ? true : false,
                railOpacity: 0.1,
                disableFadeOut: true,
            });
        });
    }

    handleHomePageTooltips() {
        $('.tip').tooltip();
        $('.tip-bottom').tooltip({
            placement: 'bottom',
        });
        $('.tip-left').tooltip({
            placement: 'left',
        });
        $('.tip-right').tooltip({
            placement: 'right',
        });
        $('.tip-focus').tooltip({
            trigger: 'focus',
        });
    }

    handlePopovers() {
        $('.pop').popover();
        $('.pop-bottom').popover({
            placement: 'bottom',
        });
        $('.pop-left').popover({
            placement: 'left',
        });
        $('.pop-top').popover({
            placement: 'top',
        });
        $('.pop-hover').popover({
            trigger: 'hover',
        });
    }

    handleAlerts() {
        $('.alert').alert();
    }

    scrollTo(el, offset) {
        const pos = el && el.length > 0 ? el.offset().top : 0;
        $('html,body').animate(
            {
                scrollTop: pos + (offset ? offset : 0),
            },
            'slow',
        );
    }

    handleTeamView() {
        this.c();
        $('.team-status-toggle').click(function (y) {
            y.preventDefault();
            w(this);
            $(this).parent().toggleClass('open');
            let z = x(this);
            $(z).slideToggle(200, function () {
                $(this).toggleClass('open');
            });
        });
        $('.team-status #teamslider').each(function () {
            $(this).slimScrollHorizontal({
                width: '100%',
                alwaysVisible: true,
                color: '#fff',
                opacity: '0.5',
                size: '5px',
            });
        });
        let w = function (y) {
            $('.team-status').each(function () {
                let z = $(this);
                if (z.is(':visible')) {
                    let A = x(y);
                    if (A != '#' + z.attr('id')) {
                        $(this).slideUp(200, function () {
                            $(this).toggleClass('open');
                            $('.team-status-toggle').each(function () {
                                let B = x(this);
                                if (B == '#' + z.attr('id')) {
                                    $(this).parent().removeClass('open');
                                }
                            });
                        });
                    }
                }
            });
        };
        let x = function (y) {
            let z = $(y).data('teamStatus');
            if (typeof z == 'undefined') {
                z = '#team-status';
            }
            return z;
        };
    }

    c() {
        $('.team-status').each(function () {
            let x = $(this);
            x.css('position', 'absolute').css('margin-top', '-1000px').show();
            let w = 0;
            $('ul li', this).each(function () {
                w += $(this).outerWidth(true) + 15;
            });
            x.css('position', 'relative').css('margin-top', '0').hide();
            $('ul', this).width(w);
        });
    }

    handleFixedSidebar() {
        let menu = $('.sidebar-menu');
        if (menu.parent('.slimScrollDiv').length === 1) {
            menu.slimScroll({
                destroy: true,
            });
            menu.removeAttr('style');
            $('#sidebar').removeAttr('style');
        }
        if ($('.sidebar-fixed').length === 0) {
            this.handleSidebarAndContentHeight();
            return;
        }
        let viewport = this.getViewPort();
        if (viewport.width >= 992) {
            let sidebarHeight = $(window).height() - $('#header').height() + 1;
            menu.slimScroll({
                size: '7px',
                color: '#a1b2bd',
                opacity: 0.3,
                height: sidebarHeight,
                allowPageScroll: false,
                disableFadeOut: false,
            });
            this.handleSidebarAndContentHeight();
        }
    }

    handleNavbarFixedTop() {
        if (this.state.is_mobile && this.state.is_fixed_header) {
            $('#main-content').addClass('margin-top-100');
        }
        if (!this.state.is_mobile && this.state.is_fixed_header) {
            $('#main-content').removeClass('margin-top-100').addClass('margin-top-50');
        }
    }

    runResponsiveFunctions() {
        for (let i in this.state.responsiveFunctions) {
            let each = this.state.responsiveFunctions[i];
            each.call();
        }
    }

    componentDidUpdate(prevProps) {
        // Auto-open has-sub-sub ที่ถูกต้องเมื่อหน้าเปลี่ยน (เช่น navigate มาที่ Escalation Settings)
        if (prevProps.page !== this.props.page) {
            this.autoOpenActiveSubSub();
        }

        // Redux user หาย (เช่น localStorage ถูกล้างจากภายนอก) แต่ currentUser ยังค้าง → ยังแสดง shell ทั้งที่ไม่มี session
        if (!this.props.user && this.state.currentUser !== undefined && this.state.currentUser !== null) {
            this.stopTokenCheck();
            this.setState({
                currentUser: undefined,
                isUser: false,
                isModerator: false,
                isAdmin: false,
                isPlanner: false,
                isServiceRequester: false,
                isServiceApprover: false,
                isFinalReviewer: false,
                isFinalApprover: false,
                isProductOwner: false,
                isCustomerRepresentative: false,
                isSales: false,
            });
            return;
        }

        const localUser = this.props.user;
        const loginType = localStorage.getItem('loginType');
        if (!localUser) {
            const { isAuthenticated, user } = this.props.auth0;
            if (isAuthenticated && user !== null) {
                const { dispatch } = this.props;
                user.username = user.email;
                dispatch(login(user, 'auth_0'))
                    .then(() => {
                        window.location.reload();
                    })
                    .catch((error) => {
                        alert(error.message);
                    });
            }
        } else {
            if (loginType === 'auth_0') {
                const { isAuthenticated } = this.props.auth0;
                clearTimeout(this.timeOut);
                this.timeOut = setTimeout(() => {
                    if (isAuthenticated === false) {
                        this.setState({ currentUser: undefined });
                        this.props.dispatch(logout());
                    }
                }, 2000);
            }
        }
    }

    stopViewAsUser(e) {
        if (e && e.preventDefault) e.preventDefault();
        AuthService.stopImpersonate()
            .then(() => window.location.reload())
            .catch((err) => {
                // If stop-impersonate fails (e.g. impersonation token already expired),
                // try to fall back to the admin backup session stored at impersonation start.
                const backup = localStorage.getItem('adminSession');
                if (backup) {
                    try {
                        const parsed = JSON.parse(backup);
                        if (parsed && parsed.accessToken) {
                            localStorage.setItem('user', backup);
                            localStorage.removeItem('adminSession');
                            window.location.reload();
                            return;
                        }
                    } catch (_) {
                        // fall through
                    }
                }
                const msg = (err.response && err.response.data && err.response.data.message) || err.message || String(err);
                alert(msg);
            });
    }

    // ========== แก้ไข logOut ==========
    logOut() {
        const { isAuthenticated } = this.props.auth0;

        this.stopTokenCheck();
        localStorage.removeItem('adminSession');

        if (isAuthenticated) {
            this.props.auth0.logout({
                returnTo: this.state.logoutURL,
            });
            history.push('/');
            window.location.reload();
        } else {
            this.setState({ currentUser: undefined });
            this.props.dispatch(logout());
            history.push('/');
            window.location.reload();
        }
    }

    activeMenu(menu, active) {
        return menu === this.props.page ? active : '';
    }

    updateUserLoggedIn = (isLoggedIn) => {
        this.setState({ userLoggedIn: isLoggedIn });
    };

    serviceRequestCallback(value, object, data) {
        const user = this.props.user;
        if (this.state.currentUser !== undefined) {
            if (object === user.email) {
                if (user.roles.includes('ROLE_SERVICE_REQUESTER') === true) {
                    this.setState({
                        checkSameUser: true,
                    });
                    history.push('/service_request/create');
                    window.location.reload();
                } else {
                    this.setState({
                        checkRole: true,
                    });
                    console.log('not permission');
                }
            } else {
                this.setState({
                    checkRole: true,
                });
                console.log('not permis ');
            }
        } else {
            if (value === true) {
                this.setState({
                    isNotUser: true,
                });
            } else {
                this.setState({
                    isNotUser: true,
                });
                console.log('not have email in CASE');
            }
        }
    }

    closeNotPermission = () => {
        this.setState({
            checkRole: false,
        });
        localStorage.removeItem('service_request');
        history.push('/');
        window.location.reload();
    };

    activeParentMenu(menu, active) {
        return menu === this.props.parent_page ? active : '';
    }

    /** True when any admin screen is active (main admin, SR admin, or SLA admin). */
    isAdministratorNavActive() {
        const p = this.props.parent_page;
        return p === 'administrator' || p === 'administrator-sr' || p === 'administrator-sla';
    }

    /**
     * Auto-open the has-sub-sub group that contains the currently active menu item (li.current).
     * Called after page/parent_page changes so the correct sub-group is shown without clicking.
     */
    autoOpenActiveSubSub() {
        setTimeout(() => {
            const currentItem = $('.sidebar-menu li.current');
            if (currentItem.length === 0) return;
            const parentSubSub = currentItem.closest('li.has-sub-sub');
            if (parentSubSub.length === 0) return;
            if (parentSubSub.hasClass('open')) return;
            // Close all other open has-sub-sub first
            const openOthers = $('.has-sub-sub.open', $('.sidebar-menu'));
            openOthers.removeClass('open');
            $('.arrow', openOthers).removeClass('open');
            openOthers.find('> ul.sub-sub').slideUp(150);
            // Open the correct has-sub-sub
            parentSubSub.addClass('open');
            parentSubSub.find('> a .arrow').addClass('open');
            parentSubSub.find('> ul.sub-sub').slideDown(200);
        }, 60);
    }

    handleButtonClick = () => {
        this.setState({ showTooltip: !this.state.showTooltip });
    };

    render() {
        const { checkSameUser, isNotUser, checkRole } = this.state;
        const {
            currentUser,
            isUser,
            isModerator,
            isProductOwner,
            isAdmin,
            release,
            isLogedAuth0,
            isServiceRequester,
            isServiceApprover,
            isPlanner,
            isFinalReviewer,
            isFinalApprover,
            isCustomerRepresentative,
            isSales,
        } = this.state;
        const { showTooltip } = this.state;

        return (
            <Router history={history}>
                {currentUser || isLogedAuth0 ? (
                    <>
                        {checkRole === true && (
                            <Suspense fallback={<RoutePageFallback />}>
                                <NotPermission onClick={this.closeNotPermission} />
                            </Suspense>
                        )}
                        <header className="navbar clearfix" id="header" style={{ zIndex: 1000 }}>
                            <div className="container">
                                <div className="navbar-brand">
                                    <Link to={'/'}>
                                        <img src={LogoHome} alt="repco-case-management" className="img-responsive" height="30" width="190" />
                                    </Link>
                                    <div id="sidebar-collapse" className="sidebar-collapse btn">
                                        <i className="fa fa-bars" data-icon1="fa fa-bars" data-icon2="fa fa-bars"></i>
                                    </div>
                                </div>
                                <ul className="nav navbar-nav pull-left hidden-xs" id="navbar-left">
                                    <li>
                                        <Link style={{ color: '#FFF' }} to={'/release-note'}>
                                            Release {release}
                                        </Link>
                                    </li>
                                </ul>
                                <ul className="nav navbar-nav pull-right">
                                    <li className="dropdown user pull-right" id="header-user">
                                        <a href="#" className="dropdown-toggle" data-toggle="dropdown">
                                            <img alt="icon-user" src="/icon-user.png" style={{ width: 20, height: 20 }} />
                                            <span className="username">{this.props.user ? this.props.user.email : localStorage.getItem('smartOFA')} </span>
                                            <i className="fa fa-angle-down"></i>
                                        </a>
                                        <ul className="dropdown-menu">
                                            <li>
                                                <Link to={'/profile'}>
                                                    <i className="fa fa-user" style={{ color: '#2F4F8D' }}></i> My Profile
                                                </Link>
                                            </li>
                                            <li>
                                                <a onClick={this.logOut}>
                                                    <i className="fa fa-power-off"></i> Log Out
                                                </a>
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </div>
                        </header>

                        {this.props.user && this.props.user.impersonatedBy ? (
                            <div
                                style={{
                                    background: '#e65100',
                                    color: '#fff',
                                    padding: '8px 16px',
                                    textAlign: 'center',
                                    fontSize: '14px',
                                    zIndex: 1001,
                                    position: 'relative',
                                }}
                            >
                                <i className="fa fa-user" style={{ marginRight: 8, color: '#fff' }} aria-hidden="true" />
                                Viewing as <strong>{this.props.user.email}</strong>
                                {this.props.user.impersonatorEmail ? (
                                    <span style={{ marginLeft: 8, opacity: 0.95 }}>(admin: {this.props.user.impersonatorEmail})</span>
                                ) : null}
                                <button
                                    type="button"
                                    className="btn btn-sm btn-default"
                                    style={{ marginLeft: 16, verticalAlign: 'middle' }}
                                    onClick={this.stopViewAsUser}
                                >
                                    Exit view as user
                                </button>
                            </div>
                        ) : null}

                        <section id="page">
                            <div id="sidebar" className="sidebar">
                                <div className="sidebar-menu nav-collapse">
                                    <div className="divide-10"></div>
                                    <ul>
                                        {isUser && (
                                            <>
                                                <li className={this.activeMenu('mycase', 'active')}>
                                                    <Link to={'/'}>
                                                        <i className="fa fa-file fa-fw"></i>
                                                        <span className="menu-text">My case</span>
                                                        <span className={this.activeMenu('mycase', 'selected')}></span>
                                                    </Link>
                                                </li>
                                                <li className={this.activeMenu('create_case', 'active')}>
                                                    <Link to={'/case/create'}>
                                                        <i className="fa fa-pencil fa-fw"></i>
                                                        <span className="menu-text">Create Case</span>
                                                        <span className={this.activeMenu('create_case', 'selected')}></span>
                                                    </Link>
                                                </li>
                                                {(isModerator || isPlanner || isServiceRequester || isServiceApprover || isFinalReviewer || isFinalApprover) && (
                                                    <>
                                                        <li className={'has-sub ' + this.activeParentMenu('mytask', 'active')}>
                                                            <Link to="#">
                                                                <i className="fa fa-list-ol fa-fw"></i>
                                                                <span className="menu-text">My Task</span>
                                                                <span className={'arrow ' + this.activeParentMenu('mytask', 'open')}></span>
                                                                <span className={this.activeParentMenu('mytask', 'selected')}></span>
                                                            </Link>
                                                            <ul className="sub">
                                                                {isModerator && (
                                                                    <li className={this.activeMenu('mytask', 'current')}>
                                                                        <Link to={'/mytask'}>
                                                                            <span className="sub-menu-text">Standard Support</span>
                                                                        </Link>
                                                                    </li>
                                                                )}
                                                                {(isServiceRequester || isPlanner || isServiceApprover || isFinalReviewer || isFinalApprover) && (
                                                                    <li className={this.activeMenu('my-task-service-request', 'current')}>
                                                                        <Link to={'/service_request/my-task-service-request'}>
                                                                            <span className="sub-menu-text">Premium Service</span>
                                                                        </Link>
                                                                    </li>
                                                                )}
                                                            </ul>
                                                        </li>
                                                    </>
                                                )}
                                            </>
                                        )}
                                        {isProductOwner && (
                                            <>
                                                <li className={this.activeMenu('validate_case', 'active')}>
                                                    <Link to={'/product_owner/validate_case'}>
                                                        <i className="fa fa-clipboard fa-fw"></i>
                                                        <span className="menu-text">Validate Case Type</span>
                                                        <span className={this.activeMenu('validate_case', 'selected')}></span>
                                                    </Link>
                                                </li>
                                                <li className={this.activeMenu('product_owner_report', 'active')}>
                                                    <Link to={'/product_owner/report'}>
                                                        <i className="fa fa-bar-chart-o fa-fw"></i>
                                                        <span className="menu-text">Report</span>
                                                        <span className={this.activeMenu('product_owner_report', 'selected')}></span>
                                                    </Link>
                                                </li>
                                            </>
                                        )}
                                        {isAdmin && (
                                            <>
                                                <li className={'has-sub ' + this.activeParentMenu('case', 'active')}>
                                                    <Link to="#">
                                                        <i className="fa fa-file-text fa-fw"></i>
                                                        <span className="menu-text">Cases</span>
                                                        <span className={'arrow ' + this.activeParentMenu('case', 'open')}></span>
                                                        <span className={this.activeParentMenu('case', 'selected')}></span>
                                                    </Link>
                                                    <ul className="sub">
                                                        <li className={this.activeMenu('case', 'current')}>
                                                            <Link to={'/case'}>
                                                                <span className="sub-menu-text">Case Management</span>
                                                            </Link>
                                                        </li>
                                                        <li className={this.activeMenu('all-service-request', 'current')}>
                                                            <Link to={'/service_request/all_service_request'}>
                                                                <span className="sub-menu-text">Service Request</span>
                                                            </Link>
                                                        </li>
                                                    </ul>
                                                </li>
                                                <li className={'has-sub ' + (this.isAdministratorNavActive() ? 'active' : '')}>
                                                    <Link to="#">
                                                        <i className="fa fa-cog fa-fw"></i>
                                                        <span className="menu-text">Administrator</span>
                                                        <span className={'arrow ' + (this.isAdministratorNavActive() ? 'open' : '')}></span>
                                                        <span className={this.isAdministratorNavActive() ? 'selected' : ''}></span>
                                                    </Link>
                                                    <ul className="sub">
                                                        <li className="has-sub-sub">
                                                            <Link to="#">
                                                                <span className="sub-menu-text">Organization &amp; product</span>
                                                                <span className="arrow"></span>
                                                            </Link>
                                                            <ul className="sub-sub">
                                                                <li className={this.activeMenu('company', 'current')}>
                                                                    <Link to={'/administrator/company'}>
                                                                        <span className="sub-sub-menu-text">Company</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('plant', 'current')}>
                                                                    <Link to={'/administrator/plant'}>
                                                                        <span className="sub-sub-menu-text">Plant</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('product_group', 'current')}>
                                                                    <Link to={'/administrator/product_group'}>
                                                                        <span className="sub-sub-menu-text">Product Group</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('product', 'current')}>
                                                                    <Link to={'/administrator/product'}>
                                                                        <span className="sub-sub-menu-text">Product</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('product_roadmap', 'current')}>
                                                                    <Link to={'/administrator/product-roadmap'}>
                                                                        <span className="sub-sub-menu-text">Product Roadmap</span>
                                                                    </Link>
                                                                </li>
                                                            </ul>
                                                        </li>
                                                        <li className="has-sub-sub">
                                                            <Link to="#">
                                                                <span className="sub-menu-text">Access &amp; case taxonomy</span>
                                                                <span className="arrow"></span>
                                                            </Link>
                                                            <ul className="sub-sub">
                                                                <li className={this.activeMenu('role', 'current')}>
                                                                    <Link to={'/administrator/role'}>
                                                                        <span className="sub-sub-menu-text">Role</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('user', 'current')}>
                                                                    <Link to={'/administrator/user'}>
                                                                        <span className="sub-sub-menu-text">User</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('issue_type', 'current')}>
                                                                    <Link to={'/administrator/issue_type'}>
                                                                        <span className="sub-sub-menu-text">Issue Type</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('issue_category', 'current')}>
                                                                    <Link to={'/administrator/issue_category'}>
                                                                        <span className="sub-sub-menu-text">Issue Category</span>
                                                                    </Link>
                                                                </li>
                                                            </ul>
                                                        </li>
                                                        <li className="has-sub-sub">
                                                            <Link to="#">
                                                                <span className="sub-menu-text">Operations &amp; tools</span>
                                                                <span className="arrow"></span>
                                                            </Link>
                                                            <ul className="sub-sub">
                                                                <li className={this.activeMenu('send_email', 'current')}>
                                                                    <Link to={'/administrator/send_email'}>
                                                                        <span className="sub-sub-menu-text">Send Email</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('bulk_update_member', 'current')}>
                                                                    <Link to={'/administrator/bulk_update_member'}>
                                                                        <span className="sub-sub-menu-text">Bulk Update Member</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('special', 'current')}>
                                                                    <Link to={'/administrator/special'}>
                                                                        <span className="sub-sub-menu-text">SR review &amp; reviewer</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('dashboard', 'current')}>
                                                                    <Link to={'/administrator/dashboard'}>
                                                                        <span className="sub-sub-menu-text">Dashboard</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('executive_report', 'current')}>
                                                                    <Link to={'/administrator/executive-report'}>
                                                                        <span className="sub-sub-menu-text">Executive report</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('complete_case', 'current')}>
                                                                    <Link to={'/administrator/complete-case'}>
                                                                        <span className="sub-sub-menu-text">Complete case</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('follow_up_task', 'current')}>
                                                                    <Link to={'/administrator/follow-up-task'}>
                                                                        <span className="sub-sub-menu-text">Follow up Task</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('service_desk_create_case', 'current')}>
                                                                    <Link to={'/administrator/service-desk-create-case'}>
                                                                        <span className="sub-sub-menu-text">Service Desk — Create Case</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('escalation_settings', 'current')}>
                                                                    <Link to={'/administrator/escalation-settings'}>
                                                                        <span className="sub-sub-menu-text">Escalation Settings</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('escalation_dashboard', 'current')}>
                                                                    <Link to={'/administrator/escalation'}>
                                                                        <span className="sub-sub-menu-text">Escalation</span>
                                                                    </Link>
                                                                </li>
                                                            </ul>
                                                        </li>
                                                        <li className="has-sub-sub">
                                                            <Link to="#">
                                                                <span className="sub-menu-text">Service request (SR)</span>
                                                                <span className="arrow"></span>
                                                            </Link>
                                                            <ul className="sub-sub">
                                                                <li className={this.activeMenu('service_type_admin', 'current')}>
                                                                    <Link to={'/administrator/service-type'}>
                                                                        <span className="sub-sub-menu-text">Service Type</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('service_category_admin', 'current')}>
                                                                    <Link to={'/administrator/service-category'}>
                                                                        <span className="sub-sub-menu-text">Service Category</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('job_role_admin', 'current')}>
                                                                    <Link to={'/administrator/job-role'}>
                                                                        <span className="sub-sub-menu-text">Job Role</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('service_approval_admin', 'current')}>
                                                                    <Link to={'/administrator/service-approval'}>
                                                                        <span className="sub-sub-menu-text">Service Approval</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('estimate_effort_reviewer_admin', 'current')}>
                                                                    <Link to={'/administrator/estimate-reviewer'}>
                                                                        <span className="sub-sub-menu-text">Estimate Reviewer</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('final_reviewer_admin', 'current')}>
                                                                    <Link to={'/administrator/final-reviewer'}>
                                                                        <span className="sub-sub-menu-text">Final Reviewer</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('final_approval_admin', 'current')}>
                                                                    <Link to={'/administrator/final-approval'}>
                                                                        <span className="sub-sub-menu-text">Final Approval</span>
                                                                    </Link>
                                                                </li>
                                                            </ul>
                                                        </li>
                                                        <li className="has-sub-sub">
                                                            <Link to="#">
                                                                <span className="sub-menu-text">SLA &amp; calendar</span>
                                                                <span className="arrow"></span>
                                                            </Link>
                                                            <ul className="sub-sub">
                                                                <li className={this.activeMenu('priority', 'current')}>
                                                                    <Link to={'/administrator/priority'}>
                                                                        <span className="sub-sub-menu-text">Priority Management</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('business-calendar', 'current')}>
                                                                    <Link to={'/administrator/holiday'}>
                                                                        <span className="sub-sub-menu-text">Business Calendar</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('sla-configuration', 'current')}>
                                                                    <Link to={'/administrator/sla-configuration'}>
                                                                        <span className="sub-sub-menu-text">SLA Targets Configuration</span>
                                                                    </Link>
                                                                </li>
                                                                <li className={this.activeMenu('sla-breach-watchlist', 'current')}>
                                                                    <Link to={'/administrator/sla-breach-watchlist'}>
                                                                        <span className="sub-sub-menu-text">SLA breach &amp; at-risk watchlist</span>
                                                                    </Link>
                                                                </li>
                                                            </ul>
                                                        </li>
                                                    </ul>
                                                </li>
                                            </>
                                        )}
                                        {isSales && (
                                            <li className={'has-sub ' + this.activeParentMenu('sales', 'active')}>
                                                <Link to="#">
                                                    <i className="fa fa-briefcase fa-fw"></i>
                                                    <span className="menu-text">Sales</span>
                                                    <span className={'arrow ' + this.activeParentMenu('sales', 'open')}></span>
                                                    <span className={this.activeParentMenu('sales', 'selected')}></span>
                                                </Link>
                                                <ul className="sub">
                                                    <li className={this.activeMenu('sales-cases', 'current')}>
                                                        <Link to={'/sales/cases'}>
                                                            <span className="sub-menu-text">My portfolio cases</span>
                                                        </Link>
                                                    </li>
                                                </ul>
                                            </li>
                                        )}
                                        {isCustomerRepresentative && (
                                            <>
                                                <li className={'has-sub ' + this.activeParentMenu('customer-company', 'active')}>
                                                    <Link to="#">
                                                        <i className="fa fa-file fa-fw"></i>
                                                        <span className="menu-text">My Company</span>
                                                        <span className={'arrow ' + this.activeParentMenu('customer-company', 'open')}></span>
                                                        <span className={this.activeParentMenu('customer-company', 'selected')}></span>
                                                    </Link>
                                                    <ul className="sub">
                                                        <li className={this.activeMenu('customer-dashboard', 'current')}>
                                                            <Link to={'/customer-report/dashboard'}>
                                                                <span className="sub-menu-text">Dashboard</span>
                                                            </Link>
                                                        </li>
                                                        <li className={this.activeMenu('customer-cases', 'current')}>
                                                            <Link to={'/customer-report/cases'}>
                                                                <span className="sub-menu-text">Cases</span>
                                                            </Link>
                                                        </li>
                                                    </ul>
                                                </li>
                                            </>
                                        )}
                                        {isUser && (
                                            <li className={this.activeMenu('legal-resource', 'active')}>
                                                <Link to={'/legal-resource'}>
                                                    <i className="fa fa-gavel fa-fw"></i>
                                                    <span className="menu-text">Legal Resources</span>
                                                    <span className={this.activeMenu('legal-resource', 'selected')}></span>
                                                </Link>
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            </div>
                            <div id="main-content">
                                <div className="container">
                                    <div className="row">
                                        <div id="content" className="col-lg-12">
                                            <div className="row">
                                                <div className="col-sm-12">
                                                    <div className="page-header">
                                                        <ul className="breadcrumb"></ul>
                                                        <div className="clearfix">
                                                            <h3 className="content-title pull-left">{this.props.title}</h3>
                                                        </div>
                                                        <div className="description">{this.props.description}</div>
                                                    </div>
                                                </div>
                                            </div>
                                            {checkSameUser === true && (
                                                <Suspense fallback={<RoutePageFallback />}>
                                                    <ServiceRequest />
                                                </Suspense>
                                            )}
                                            <AuthenticatedRoutes serviceRequestCallback={this.serviceRequestCallback} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className={`floating-button ${window.innerWidth < 992 ? 'small' : ''}`}>
                                {showTooltip && (
                                    <div className="description">
                                        <div className="topic-des">
                                            <p>
                                                <i className="fa fa-question-circle"></i>Help & Documentation
                                            </p>
                                        </div>
                                        <div className="container-fluid"></div>
                                        <div className="show-role">
                                            <p>
                                                <i className="fa fa-user"></i> {isAdmin || isModerator ? 'Responsible' : 'Requester'}
                                            </p>
                                        </div>
                                        <div className="des">
                                            <a
                                                href={isAdmin || isModerator ? ResponsibleDoc : RequesterDoc}
                                                download={isAdmin || isModerator ? 'ManualEN_Responsible_v.pdf' : 'ManualEN_Requester_v 1.9.0.pdf'}
                                                target="_blank"
                                            >
                                                <img src={TestImg}></img>
                                                {isAdmin || isModerator ? 'ManualEN_Responsible_v.pdf' : 'ManualEN_Requester_v 1.9.0.pdf'}
                                            </a>
                                        </div>
                                    </div>
                                )}
                                <button onClick={this.handleButtonClick}>
                                    <i className="fa fa-question">
                                        <span className={`tooltip ${showTooltip ? 'hidden' : ''}`}>Help and documentations!</span>
                                    </i>
                                </button>
                            </div>
                        </section>
                    </>
                ) : (
                    <>
                        <section id="page">
                            <header>
                                <div className="container">
                                    <div className="row">
                                        <div className="col-md-4 col-md-offset-4">
                                            <div id="logo">
                                                <Link to={'/'}>
                                                    <img src={Logo} height="40" alt="repco-case-management" />
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </header>
                            {isNotUser === true && (
                                <Suspense fallback={<RoutePageFallback />}>
                                    <NotUser />
                                </Suspense>
                            )}
                            <MsalProvider instance={this.props.instance}>
                                <Suspense fallback={<RoutePageFallback />}>
                                    <Routes>
                                        <Route path="/" element={<Login instance={this.props.instance} />} />
                                        <Route path="/login" element={<Login instance={this.props.instance} />} />
                                        <Route path="/register" element={<Register />} />
                                        <Route path="/service_request_integration" element={<ServiceRequestIntegration valueCallback={this.serviceRequestCallback} />} />
                                        <Route path="/public/escalation/:token/case/:caseId" element={<EscalationPublicCaseView />} />
                                        <Route path="/public/escalation/:token" element={<EscalationPublicView />} />
                                    </Routes>
                                </Suspense>
                            </MsalProvider>
                        </section>
                    </>
                )}
            </Router>
        );
    }
}

function mapStateToProps(state) {
    const { user, loginType } = state.auth;
    const { title, description } = state.title;
    const { page, parent_page } = state.page;
    return {
        user,
        loginType,
        title,
        description,
        page,
        parent_page,
    };
}

export default connect(mapStateToProps)(withAuth0(App));
